import { useCallback, useMemo } from "react";
import { MapLocation, TripLegMode } from "types/map";
import { hasLatLong, neutralMapLocation } from "libraries/map";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  appendReferenceTripDestination,
  registerReferenceTripDestination,
  registerReferenceTripFrom,
  removeReferenceTripDestination,
  selectReferenceTripDestinations,
  selectReferenceTripFrom,
  selectReferenceTripLegModes,
  setReferenceTripDestinations,
  setReferenceTripLegMode,
} from "redux/slices/shed/shedSlice";
import { padTripLegModes } from "libraries/tripLeg";

const MAX_DESTINATIONS = 26;

export type TripStopRole = "start" | "end" | "stop";

export type TripStopCardItem = {
  id: string;
  role: TripStopRole;
  roleLabel: string;
  location: MapLocation;
  destinationIndex?: number;
  incomingLegIndex?: number;
  incomingLegMode?: TripLegMode;
  previousLocation?: MapLocation;
};

type ReturnType = {
  stops: TripStopCardItem[];
  canAddStop: boolean;
  handleAddStop: (location: MapLocation) => void;
  handleRemoveStop: (stop: TripStopCardItem) => void;
  handleReorderStops: (fromIndex: number, toIndex: number) => void;
  handleSetLegMode: (incomingLegIndex: number, mode: TripLegMode) => void;
};

const useTripPlannerStops = (): ReturnType => {
  const dispatch = useAppDispatch();
  const from = useAppSelector(selectReferenceTripFrom);
  const destinations = useAppSelector(selectReferenceTripDestinations);
  const storedLegModes = useAppSelector(selectReferenceTripLegModes);

  const stops = useMemo((): TripStopCardItem[] => {
    const items: TripStopCardItem[] = [];

    if (hasLatLong(from)) {
      items.push({
        id: "from",
        role: "start",
        roleLabel: "Start",
        location: from,
      });
    }

    const filled = destinations
      .map((location, index) => ({ location, index }))
      .filter(({ location }) => hasLatLong(location));
    const legModes = padTripLegModes(storedLegModes, filled.length);

    filled.forEach(({ location, index }, filledIndex) => {
      const isLast = filledIndex === filled.length - 1;
      const role: TripStopRole = isLast ? "end" : "stop";
      const roleLabel = isLast ? "End" : `Stop ${filledIndex + 1}`;
      const previousLocation =
        filledIndex === 0
          ? hasLatLong(from)
            ? from
            : undefined
          : filled[filledIndex - 1].location;

      items.push({
        id: `destination-${index}`,
        role,
        roleLabel,
        location,
        destinationIndex: index,
        incomingLegIndex: filledIndex,
        incomingLegMode: legModes[filledIndex],
        previousLocation,
      });
    });

    return items;
  }, [from, destinations, storedLegModes]);

  const canAddStop =
    !hasLatLong(from) ||
    destinations.some((d) => !hasLatLong(d)) ||
    destinations.length < MAX_DESTINATIONS;

  const handleAddStop = useCallback(
    (location: MapLocation) => {
      if (!hasLatLong(location)) {
        return;
      }

      if (!hasLatLong(from)) {
        dispatch(registerReferenceTripFrom(location));
        return;
      }

      const lastIndex = destinations.length - 1;
      const last = destinations[lastIndex];

      if (last && !hasLatLong(last)) {
        dispatch(
          registerReferenceTripDestination({ index: lastIndex, location })
        );
        return;
      }

      if (destinations.length >= MAX_DESTINATIONS) {
        return;
      }

      dispatch(appendReferenceTripDestination(location));
    },
    [dispatch, from, destinations]
  );

  const handleRemoveStop = useCallback(
    (stop: TripStopCardItem) => {
      if (stop.id === "from") {
        dispatch(registerReferenceTripFrom(neutralMapLocation()));
        return;
      }

      const index = stop.destinationIndex;
      if (index === undefined) {
        return;
      }

      if (destinations.length <= 1) {
        dispatch(setReferenceTripDestinations([neutralMapLocation()]));
        return;
      }

      dispatch(removeReferenceTripDestination(index));
    },
    [dispatch, destinations.length]
  );

  const handleReorderStops = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= stops.length ||
        toIndex >= stops.length ||
        stops.length < 2
      ) {
        return;
      }

      const ordered = stops.map((stop) => stop.location);
      const [moved] = ordered.splice(fromIndex, 1);
      ordered.splice(toIndex, 0, moved);

      const [nextFrom, ...nextDestinations] = ordered;
      dispatch(registerReferenceTripFrom(nextFrom));
      dispatch(
        setReferenceTripDestinations(
          nextDestinations.length > 0
            ? nextDestinations
            : [neutralMapLocation()]
        )
      );
    },
    [dispatch, stops]
  );

  const handleSetLegMode = useCallback(
    (incomingLegIndex: number, mode: TripLegMode) => {
      dispatch(setReferenceTripLegMode({ index: incomingLegIndex, mode }));
    },
    [dispatch]
  );

  return {
    stops,
    canAddStop,
    handleAddStop,
    handleRemoveStop,
    handleReorderStops,
    handleSetLegMode,
  };
};

export default useTripPlannerStops;
